import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

type Status = 'idle' | 'submitting' | 'done' | 'error'

export default function ConfirmDeletion() {
  const [params]           = useSearchParams()
  const token              = useMemo(() => params.get('token'), [params])
  const [status, setStatus] = useState<Status>('idle')
  const [error,  setError]  = useState('')

  useEffect(() => { document.title = 'Confirm account deletion — PantryIO' }, [])

  async function handleConfirm() {
    if (!token) return
    setStatus('submitting')
    setError('')

    try {
      const res = await fetch('/api/confirm-account-deletion', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token }),
      })

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}))
        setError(payload.error || 'Something went wrong. Please try again.')
        setStatus('error')
        return
      }

      setStatus('done')
    } catch {
      setError('Network error. Please check your connection and try again.')
      setStatus('error')
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">

      {/* Header */}
      <header className="px-6 py-4 border-b border-border-sand">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-[9px] bg-forest text-forest-ink font-serif italic text-[16px] font-semibold leading-none">
            P
          </span>
          <span className="font-serif text-[15px] font-medium text-ink italic">Pantry</span>
        </Link>
      </header>

      <main className="flex-1 px-6 py-8 sm:py-16 flex items-start sm:items-center justify-center">
        <div className="w-full max-w-[520px]">

          {!token ? (
            <article className="bg-paper border border-border-sand rounded-2xl p-6 sm:p-8 text-center">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-critical mb-2">
                Error
              </div>
              <h1 className="font-serif text-[24px] text-ink leading-[1.2] tracking-[-0.01em] mb-3">
                Invalid deletion <em className="italic">link</em>.
              </h1>
              <p className="font-serif text-[15px] italic text-ink-muted leading-[1.55] mb-6">
                This link is missing its confirmation token. Please use the link from your confirmation email, or start over.
              </p>
              <Link
                to="/delete-account"
                className="inline-block rounded-[14px] bg-forest text-forest-ink py-3 px-6 text-[14px] font-bold tracking-[-0.01em]"
              >
                Start over
              </Link>
            </article>
          ) : status === 'done' ? (
            <article className="bg-paper border border-border-sand rounded-2xl p-6 sm:p-8 text-center">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                Done
              </div>
              <h1 className="font-serif text-[26px] text-ink leading-[1.15] tracking-[-0.01em] mb-3">
                Your account has been <em className="italic">deleted</em>.
              </h1>
              <p className="font-serif text-[15px] italic text-ink-muted leading-[1.55] mb-6">
                Thank you for using PantryIO.
              </p>
              <Link
                to="/"
                className="inline-block rounded-[14px] border border-border-sand bg-paper py-3 px-6 text-[14px] font-semibold text-ink"
              >
                Return to home
              </Link>
            </article>
          ) : (
            <article className="bg-paper border border-border-sand rounded-2xl p-6 sm:p-8">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-critical mb-2">
                Confirm
              </div>
              <h1 className="font-serif text-[26px] text-ink leading-[1.15] tracking-[-0.01em] mb-3">
                Confirm account <em className="italic">deletion</em>
              </h1>
              <p className="font-serif text-[15px] text-ink leading-[1.6] mb-6">
                This will <strong className="font-semibold">permanently delete</strong> your PantryIO account and all associated data. This cannot be undone.
              </p>

              {error && (
                <p className="rounded-[12px] bg-critical-bg border border-critical/20 text-critical text-[13px] p-3 mb-4">
                  {error}
                </p>
              )}

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={status === 'submitting'}
                  className="w-full rounded-[14px] bg-critical text-cream py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {status === 'submitting' && (
                    <span className="h-4 w-4 rounded-full border-2 border-cream border-t-transparent animate-spin" />
                  )}
                  {status === 'submitting' ? 'Deleting…' : 'Yes, delete my account'}
                </button>
                <Link
                  to="/"
                  className="w-full rounded-[14px] border border-border-sand bg-paper py-3 text-[14px] font-semibold text-ink text-center"
                >
                  Cancel
                </Link>
              </div>
            </article>
          )}

        </div>
      </main>
    </div>
  )
}
